import React, { useEffect, useId, useRef } from "react";
import styled, { css, keyframes } from "styled-components";
import { Typography } from "../../general/Typography";

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  description?: string;
  error?: string;
  indeterminate?: boolean;
}

const popIn = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const CheckboxWrapper = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    user-select: none;

`;

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  display: none;
`;

const StyledBox = styled.div<{
    checked?: boolean;
    error?: boolean;
    indeterminate?: boolean;
  }>`
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: ${({ theme }) => theme.radii.sm};
    border: 2px solid
      ${({ theme, error, checked }) =>
        error
          ? theme.colors.error
          : checked
          ? theme.colors.primary
          : theme.colors.border};
    background: ${({ theme, checked, indeterminate }) =>
      checked || indeterminate ? theme.colors.primary : theme.colors.surface};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    box-shadow: ${({ checked, theme }) =>
      checked ? theme.elevations.low : "none"};
  
    svg {
      animation: ${popIn} 0.2s ease;
    }
  `;

const Checkmark = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <path
      d="M4 10.5L8.5 15L16 6"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LabelContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; /* ✅ Vertical centering */
  gap: 2px;
`;


const IndeterminateMark = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
      <line
        x1="5"
        y1="10"
        x2="15"
        y2="10"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
  export const Checkbox: React.FC<CheckboxProps> = ({
    label,
    description,
    checked,
    onChange,
    error,
    disabled,
    indeterminate,
    ...rest
  }) => {
    const id = useId(); // 🔑 generate unique ID
    const checkboxRef = useRef<HTMLInputElement>(null);
  
    useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate]);
  
    return (
      <CheckboxWrapper htmlFor={id} disabled={disabled} as="label">
        <span style={{ display: "flex", alignItems: "center" }}>
          <HiddenCheckbox
            id={id}
            ref={checkboxRef}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            {...rest}
          />
          <StyledBox checked={checked} error={!!error} indeterminate={indeterminate}>
            {indeterminate ? <IndeterminateMark /> : checked ? <Checkmark /> : null}
          </StyledBox>
        </span>
        <LabelContent>
          {label && (
            <Typography as="span" variant="body" weight="medium">
              {label}
            </Typography>
          )}
          {description && (
            <Typography as="span" variant="caption" colorScheme="textSecondary">
              {description}
            </Typography>
          )}
          {error && (
            <Typography as="span" variant="caption" colorScheme="error">
              {error}
            </Typography>
          )}
        </LabelContent>
      </CheckboxWrapper>
    );
  };